import { api, type ApiResponse } from './api'

/**
 * Cliente del catálogo.
 *
 * Productos y sets van cada uno a su endpoint, para lectura y para escritura. La
 * lectura estuvo unificada en `/catalog` con `kind` como discriminante, y se
 * separó al dar a cada tipo su pantalla: un listado único obligaba a filtrar por
 * tipo **en el cliente**, sobre la página ya paginada, así que una página podía
 * traer menos elementos de los que decía la paginación y el total que se veía era
 * el del catálogo entero.
 *
 * Lo único que sigue siendo único es el buscador del cotizador, y ahora pide el
 * tipo explícitamente en vez de mezclar los resultados.
 */

/** De qué tipo es lo que se busca o lo que se está editando. */
export type CatalogKind = 'product' | 'set'

/** A qué pantalla del catálogo pertenece una operación. */
export type CatalogKindPlural = 'products' | 'sets'

/** Tipo de set. Los productos no tienen tipo. */
export type SetType = 'nacional' | 'premium'

interface Timestamped {
  created_at: string | null
  updated_at: string | null
}

/**
 * Campos comunes de productos y sets.
 *
 * `retail_price` y `wholesale_price` llegan como string decimal desde el backend y
 * se formatean en el componente, no aquí: formatear en el servicio perdería el
 * valor original para recálculos y para el payload de la cotización.
 */
export interface CatalogEntry {
  id: number
  reference: string
  name: string
  retail_price: string
  wholesale_price: string
}

export interface Product extends CatalogEntry, Timestamped {}

export interface CatalogSet extends CatalogEntry, Timestamped {
  type: SetType
}

/**
 * Un elemento tal y como lo devuelve el buscador, que sí lleva el discriminante.
 *
 * Solo el buscador lo necesita: cada pantalla ya sabe de qué tipo está hecha, así
 * que en un listado el discriminante sería redundante.
 */
export interface CatalogSearchHit extends CatalogEntry {
  kind: CatalogKind
  type?: SetType
}

export interface CatalogMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  query: Record<string, unknown>
}

export interface ProductPage {
  data: Product[]
  meta: CatalogMeta
}

export interface SetPage {
  data: CatalogSet[]
  meta: CatalogMeta
}

/** Payload de alta. Solo el servidor decide si los campos son válidos. */
export interface ProductPayload {
  reference: string
  name: string
  retail_price: number
  wholesale_price: number
}

export interface SetPayload extends ProductPayload {
  type: SetType
}

/**
 * Payload que emite el formulario del catálogo, ya normalizado a números y con
 * `kind` resuelto. `type` viaja solo cuando el registro es un set.
 */
export interface CatalogFormPayload {
  kind: CatalogKind
  reference: string
  name: string
  retail_price: number
  wholesale_price: number
  type?: SetType
}

/**
 * Parámetros del buscador del cotizador.
 *
 * `kind` es obligatorio porque sin él no hay forma de saber de qué tabla buscar, y
 * `type` filtra dentro de la tabla de sets. No son dos criterios para lo mismo: uno
 * elige la tabla y el otro filtra la categoría.
 */
export interface CatalogSearchParams {
  q: string
  kind: CatalogKind
  /**
   * Nacional o Premium, solo para `kind: 'set'`.
   *
   * Con productos se manda vacío y el servidor lo ignora: la tabla no tiene columna de
   * tipo y el buscador de producto no ofrece el filtro.
   */
  type?: SetType
}

/**
 * Envelope del listado: los datos y los metadatos de paginación.
 *
 * Solo lo usan `ProductPage` y `SetPage`, cada una con su propio tipo de fila. La
 * versión con parámetro se quedó sin usar al separarse los listados, y un alias
 * genérico que nadie instancia es una forma de tener dos verdades sobre la misma
 * envoltura.
 */

/**
 * Descarta los filtros vacíos antes de la petición.
 *
 * `search: ''` llega al backend como parámetro presente, y el listado filtraría por
 * el string vacío en vez de devolver el catálogo completo. Enviar solo lo que tiene
 * contenido deja la URL limpia y evita ese 422.
 */
function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

/** Convierte el decimal string del backend en número para el payload de escritura. */
export function toPriceNumber(value: string): number {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Buscador del cotizador.
 *
 * Devuelve la `AxiosResponse` tal cual, con el envelope del proyecto dentro de
 * `response.data`: el controlador ya envuelve en `{ data, meta }` y reenvolver
 * aquí obligaría a las vistas a leer `data.data.data`.
 */
export const catalogApi = {
  /**
   * Hasta 10 coincidencias **de un solo tipo**.
   *
   * Antes devolvía las de productos y sets mezcladas, y eso obligaba a leer el
   * discriminante de cada fila del desplegable para saber si lo que se estaba
   * añadiendo era la pieza que hacía falta o el conjunto que la contenía.
   */
  search(params: CatalogSearchParams) {
    return api.get<ApiResponse<CatalogSearchHit[]>>('/catalog/search', { params: withoutEmpty(params) })
  },
}

/** CRUD de productos. */
export const productApi = {
  list(params: { search?: string; page?: number; per_page?: number } = {}) {
    return api.get<ProductPage>('/products', { params: withoutEmpty(params) })
  },

  get(id: number) {
    return api.get<ApiResponse<Product>>(`/products/${id}`)
  },

  create(payload: ProductPayload) {
    return api.post<ApiResponse<Product>>('/products', payload)
  },

  update(id: number, payload: Partial<ProductPayload>) {
    return api.patch<ApiResponse<Product>>(`/products/${id}`, payload)
  },

  remove(id: number) {
    return api.delete(`/products/${id}`)
  },
}

/** CRUD de sets. */
export const setApi = {
  list(params: { search?: string; type?: SetType; page?: number; per_page?: number } = {}) {
    return api.get<SetPage>('/sets', { params: withoutEmpty(params) })
  },

  get(id: number) {
    return api.get<ApiResponse<CatalogSet>>(`/sets/${id}`)
  },

  create(payload: SetPayload) {
    return api.post<ApiResponse<CatalogSet>>('/sets', payload)
  },

  update(id: number, payload: Partial<SetPayload>) {
    return api.patch<ApiResponse<CatalogSet>>(`/sets/${id}`, payload)
  },

  remove(id: number) {
    return api.delete(`/sets/${id}`)
  },
}

/**
 * Elimina un elemento del catálogo según su `kind`.
 *
 * Quien llama tiene que saber el tipo, porque los borrados van a endpoints
 * distintos. Si un borrado usara siempre el de productos, un set devolvería 404 o,
 * peor, borraría el producto homónimo. La referencia es única **por tabla**, así
 * que el mismo `id` puede existir en las dos.
 */
export async function removeCatalogItem(item: { id: number; kind: CatalogKind }): Promise<unknown> {
  return item.kind === 'set' ? setApi.remove(item.id) : productApi.remove(item.id)
}
